import { useEffect }                        from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer, toast }           from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './index.css';

import { AuthProvider }             from './context/AuthContext';
import { useAuth }                  from './context/AuthContext';
import { consumeMsalRedirectResult } from './config/msalConfig';
import PWAInstallPrompt             from './components/PWAInstallPrompt';
import OfflineBanner                from './components/OfflineBanner';
import PrivateRoute                 from './components/PrivateRoute';
import Navbar                       from './components/Navbar';
import ReceptionLayout              from './components/ReceptionLayout';

import Login               from './pages/Login';
import Dashboard           from './pages/Dashboard';
import VisitorRegistration from './pages/VisitorRegistration';
import Records             from './pages/Records';
import Reports             from './pages/Reports';
import Users               from './pages/Users';
import Offices             from './pages/Offices';
import Kiosk               from './pages/Kiosk';

/* ── Handles the auth result when returning from Microsoft loginRedirect() ── */
const MsalRedirectHandler = () => {
  const { loginWithSSO, isAuthenticated } = useAuth();
  useEffect(() => {
    if (isAuthenticated) return;
    const result = consumeMsalRedirectResult();
    if (result?.idToken) {
      loginWithSSO(result.idToken)
        .then(() => toast.success('Signed in with Microsoft!'))
        .catch(() => toast.error('Microsoft account not authorised. Contact your admin.'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

/* Layout wrapper for authenticated pages */
const AppLayout = ({ children }) => (
  <div className="d-flex flex-column min-vh-100" style={{ background: '#f4f6fb' }}>
    <Navbar />
    <OfflineBanner />
    <main className="flex-grow-1">
      {children}
    </main>
    <footer className="py-3 text-center text-muted small border-top bg-white">
      <span style={{ color: '#F2652C', fontWeight: 600 }}>THINK GAS</span>
      <span className="text-muted mx-2">·</span>
      <span className="text-muted">Visitor Entry Portal</span>
      <span className="text-muted mx-2">·</span>
      &copy; {new Date().getFullYear()} All rights reserved
    </footer>
  </div>
);

/* Guards use ReceptionLayout; all others use AppLayout */
const GuardRoute = ({ children }) => {
  const { user } = useAuth();
  return user?.role === 'guard'
    ? <ReceptionLayout>{children}</ReceptionLayout>
    : <AppLayout>{children}</AppLayout>;
};

/* Root route — guards go straight to /register */
const RootRoute = () => {
  const { user } = useAuth();
  if (user?.role === 'guard') return <Navigate to="/register" replace />;
  return <AppLayout><Dashboard /></AppLayout>;
};

const App = () => (
  <AuthProvider>
    {/* Process Microsoft redirect result before any route renders */}
    <MsalRedirectHandler />
    <BrowserRouter>
      <Routes>
        {/* ── Public routes ── */}
        <Route path="/login" element={<Login />} />

        {/* Public kiosk — no authentication required */}
        <Route path="/kiosk" element={<Kiosk />} />

        {/* ── Protected routes ── */}
        <Route path="/" element={
          <PrivateRoute>
            <RootRoute />
          </PrivateRoute>
        } />

        <Route path="/register" element={
          <PrivateRoute roles={['guard', 'superadmin']}>
            <GuardRoute><VisitorRegistration /></GuardRoute>
          </PrivateRoute>
        } />

        <Route path="/records" element={
          <PrivateRoute>
            <GuardRoute><Records /></GuardRoute>
          </PrivateRoute>
        } />

        <Route path="/reports" element={
          <PrivateRoute>
            <AppLayout><Reports /></AppLayout>
          </PrivateRoute>
        } />

        <Route path="/users" element={
          <PrivateRoute minRole="location_admin">
            <AppLayout><Users /></AppLayout>
          </PrivateRoute>
        } />

        {/* Superadmin only */}
        <Route path="/offices" element={
          <PrivateRoute roles={['superadmin']}>
            <AppLayout><Offices /></AppLayout>
          </PrivateRoute>
        } />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <PWAInstallPrompt />
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme="light"
        limit={3}
        toastStyle={{ borderRadius: 12, fontSize: 14 }}
      />
    </BrowserRouter>
  </AuthProvider>
);

export default App;
